import { CopyableText } from '@/components/copyable-text/CopyableText';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import { loadEnvironmentBuiltinByDocsId } from '@/shared/constant/local-storage/use-environment/UseBuiltinEnvironmentStorage';
import clsx from 'clsx';
import type React from 'react';
import { useMemo } from 'react';
import VariableTag from './components/VariableTag';
import { useStorageType } from '@/hooks/useStorageType';

type VariablesTextProps = {
  text: string;
  className?: string;
  tagClickable?: boolean;
  copyable?: boolean;
  copyableText?: string;
  style?: React.CSSProperties;
  onTagClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
};

export const VariablesText: React.FC<VariablesTextProps> = ({
  text,
  className,
  tagClickable = true,
  copyable = false,
  copyableText,
  style,
  onTagClick,
}) => {
  const selectedEnvironmentId = useDocsDataModelStore(
    (x) => x.environments.selectedEnvironment?.id,
  );
  const environmentList = useDocsDataModelStore((x) => x.environments.environmentList);
  const docsId = useDocsDataModelStore((x) => x.docsIdTypeData.onlineId);
  const setEnvironmentModalVisible = useOperationConfigModelStore(
    (x) => x.setEnvironmentModalVisible,
  );
  const setEnvironmentModalFocusKey = useOperationConfigModelStore(
    (x) => x.setEnvironmentModalFocusKey,
  );
  const selectedEnvironment = useMemo(() => {
    return environmentList.find((x) => x.id === selectedEnvironmentId);
  }, [environmentList, selectedEnvironmentId]);

  const parts = useMemo(() => {
    if (!text) return [];
    return text.split(/(\{\{[^{}]+\}\})/g);
  }, [text]);

  const storageType = useStorageType();

  if (!text) return null;

  return (
    <span className={clsx(className)} style={{ fontSize: 14, ...style }}>
      {parts.map((part) => {
        if (part.match(/^\{\{[^{}]+\}\}$/)) {
          const variableName = part.slice(2, -2);
          let variables = selectedEnvironment?.variables;
          if (!variables) {
            const environmentBuiltin = loadEnvironmentBuiltinByDocsId(
              docsId,
              storageType,
            );
            variables = environmentBuiltin.variables;
          }
          const value = variables?.find((x) => x.name === variableName)?.value;
          let isRendered = false;
          if (value) isRendered = true;

          return (
            <VariableTag
              key={part}
              className={
                isRendered ? 'border-primary-3 text-primary-8' : 'border-transparent text-primary-6'
              }
              variableTag={part}
              variableName={variableName}
              variableValue={isRendered ? value : undefined}
              onClick={(e) => {
                if (!tagClickable) return;
                setEnvironmentModalVisible(true);
                setEnvironmentModalFocusKey(variableName);
                onTagClick?.(e);
              }}
              clickable={tagClickable}
            />
          );
        }

        if (copyable) {
          return <CopyableText text={copyableText || part} content={part} key={part} />;
        }

        return part;
      })}
    </span>
  );
};
